import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * Home-page articles carousel with horizontal scroll snap + arrow controls
 * + a "View all" CTA. Lazy-loads /api/articles.
 */
export default function ArticlesCarousel() {
  const [items, setItems] = useState([]);
  const trackRef = useRef(null);

  useEffect(() => {
    api
      .get("/articles")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        // Only surface articles that have a real cover image, drop placeholder cards
        setItems(list.filter((a) => a && typeof a.cover_url === "string" && a.cover_url.trim().length > 0).slice(0, 9));
      })
      .catch(() => {});
  }, []);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const w = el.clientWidth;
    el.scrollBy({ left: dir * (w * 0.85), behavior: "smooth" });
  };

  if (items.length === 0) return null;

  return (
    <section className="md-section md-articles-carousel-section" data-testid="home-articles-carousel">
      <div className="md-container">
        <div className="md-articles-carousel-head">
          <div>
            <div className="md-label-eyebrow mb-2">From the Journal</div>
            <h2 className="text-3xl md:text-5xl font-serif">
              Read, learn, return.
            </h2>
            <p className="text-base mt-3" style={{ color: "var(--md-text-soft)", maxWidth: 520 }}>
              Long-form pieces on therapy, cognitive performance and psychology careers, written by our clinicians and mentors.
            </p>
          </div>
          <div className="md-articles-carousel-actions">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="md-articles-arrow"
              aria-label="Previous articles"
              data-testid="articles-carousel-prev"
            >
              <ArrowLeft size={16} weight="bold" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="md-articles-arrow"
              aria-label="Next articles"
              data-testid="articles-carousel-next"
            >
              <ArrowRight size={16} weight="bold" />
            </button>
            <Link to="/articles" className="btn-primary md-articles-viewall" data-testid="articles-carousel-viewall">
              View all <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>

        <div className="md-articles-carousel-track" ref={trackRef} data-testid="articles-carousel-track">
          {items.map((a) => (
            <Link
              key={a.id || a.slug}
              to={`/articles/${a.slug}`}
              className="md-articles-card"
              data-testid={`home-article-card-${a.slug}`}
            >
              <div className="md-articles-card-img">
                <img src={a.cover_url} alt={a.title} loading="lazy" />
                <span className="md-articles-card-tag">{a.category || "Journal"}</span>
              </div>
              <div className="md-articles-card-body">
                <h3 className="font-serif text-lg md-articles-card-title">{a.title}</h3>
                <p className="md-articles-card-excerpt">{a.excerpt}</p>
                <span className="md-articles-card-meta">
                  By {a.author || "MindDhara"} <ArrowRight size={12} weight="bold" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
